import { makeAuthenticatedRequest } from './authService';
import { logApiConfig } from '../config/api';

// Log API configuration in development
if (process.env.NODE_ENV === 'development') {
  logApiConfig();
}

export interface FinanceAsset {
  id: number;
  user_id: number;
  name: string;
  notes?: string;
  category: 'stocks' | 'bonds' | 'real_estate' | 'commodities' | 'gold' | 'crypto' | 'cash' | 'retirement' | 'insurance' | 'collectibles' | 'business' | 'other' | 'mortgage' | 'credit_card' | 'personal_loan' | 'student_loan' | 'auto_loan' | 'business_loan' | 'other_debt';
  sub_category?: string;
  value: number;
  valuation_date: string;
  valuation_month: string;
  created_at: string;
  updated_at: string;
}

export interface FinanceAssetFormData {
  name: string;
  notes?: string;
  category?: 'stocks' | 'bonds' | 'real_estate' | 'commodities' | 'crypto' | 'cash' | 'retirement' | 'insurance' | 'collectibles' | 'business' | 'other' | 'mortgage' | 'credit_card' | 'personal_loan' | 'student_loan' | 'auto_loan' | 'business_loan' | 'other_debt';
  sub_category?: string;
  value: number;
  valuation_date: string;
  valuation_month: string;
}

export interface CreateFinanceAssetData {
  user_id: string;
  name: string;
  notes?: string;
  category?: 'stocks' | 'bonds' | 'real_estate' | 'commodities' | 'crypto' | 'cash' | 'retirement' | 'insurance' | 'collectibles' | 'business' | 'other' | 'mortgage' | 'credit_card' | 'personal_loan' | 'student_loan' | 'auto_loan' | 'business_loan' | 'other_debt';
  sub_category?: string;
  value: number;
  valuation_date: string;
  valuation_month: string;
}

export interface UpdateFinanceAssetData {
  id: string;
  name?: string;
  notes?: string;
  category?: 'stocks' | 'bonds' | 'real_estate' | 'commodities' | 'crypto' | 'cash' | 'retirement' | 'insurance' | 'collectibles' | 'business' | 'other' | 'mortgage' | 'credit_card' | 'personal_loan' | 'student_loan' | 'auto_loan' | 'business_loan' | 'other_debt';
  sub_category?: string;
  value?: number;
  valuation_date?: string;
  valuation_month?: string;
}

export interface FinanceAssetFilters {
  user_id: string;
  category?: 'stocks' | 'bonds' | 'real_estate' | 'commodities' | 'crypto' | 'cash' | 'retirement' | 'insurance' | 'collectibles' | 'business' | 'other' | 'mortgage' | 'credit_card' | 'personal_loan' | 'student_loan' | 'auto_loan' | 'business_loan' | 'other_debt';
  sub_category?: string;
  valuation_month?: string;
  search_term?: string;
  limit?: number;
  offset?: number;
}

export interface PortfolioSummary {
  user_id: number;
  category: string;
  valuation_month: string;
  asset_count: number;
  total_value: number;
  average_value: number;
  min_value: number;
  max_value: number;
}

export interface AssetGrowthData {
  valuation_month: string;
  current_value: number;
  previous_value?: number;
  growth_amount?: number;
  growth_percentage?: number;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface PortfolioValueData {
  valuation_month: string;
  total_portfolio_value: number;
  previous_portfolio_value?: number;
  growth_amount?: number;
  growth_percentage?: number;
  asset_count: number;
  average_asset_value: number;
}

export interface AssetLog {
  id: number;
  asset_id: number;
  user_id: number;
  old_value?: number;
  new_value: number;
  valuation_date: string;
  valuation_month: string;
  notes?: string;
  created_at: string;
  asset_name?: string;
  category?: string;
  sub_category?: string;
  value_change?: number;
  percentage_change?: number;
}

export interface AssetValueHistory {
  valuation_date: string;
  valuation_month: string;
  current_value: number;
  previous_value?: number;
  growth_amount?: number;
  growth_percentage?: number;
  notes?: string;
  created_at: string;
}

export interface PortfolioChange {
  change_date: string;
  total_changes: number;
  total_value_change: number;
  avg_percentage_change?: number;
  assets_changed: number;
}

export interface AssetLogFilters {
  user_id: string;
  asset_id?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}

class FinanceAssetsService {
  // Create a new finance asset
  async createFinanceAsset(userId: string, assetData: FinanceAssetFormData): Promise<{ success: boolean; message?: string; id?: number }> {
    try {
      const data: CreateFinanceAssetData = {
        user_id: userId,
        name: assetData.name,
        notes: assetData.notes || undefined,
        category: assetData.category || 'other',
        sub_category: assetData.sub_category || undefined,
        value: assetData.value,
        valuation_date: assetData.valuation_date,
        valuation_month: assetData.valuation_month
      };

      const response = await makeAuthenticatedRequest('/assets', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      
      return response;
    } catch (error) {
      console.error('Error creating finance asset:', error);
      throw error;
    }
  }

  // Get finance asset by ID
  async getFinanceAssetById(assetId: string): Promise<{ success: boolean; asset?: FinanceAsset }> {
    try {
      const response = await makeAuthenticatedRequest(`/assets/${assetId}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching finance asset:', error);
      throw error;
    }
  }

  // Get all finance assets for a user
  async getUserFinanceAssets(filters: FinanceAssetFilters): Promise<{ success: boolean; assets?: FinanceAsset[]; count?: number }> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (filters.category) queryParams.append('category', filters.category);
      if (filters.sub_category) queryParams.append('sub_category', filters.sub_category);
      if (filters.valuation_month) queryParams.append('valuation_month', filters.valuation_month);
      if (filters.limit) queryParams.append('limit', filters.limit.toString());
      if (filters.offset) queryParams.append('offset', filters.offset.toString());
      
      const queryString = queryParams.toString();
      const url = `/assets/user/${filters.user_id}${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching user finance assets:', error);
      throw error;
    }
  }

  // Update a finance asset
  async updateFinanceAsset(updateData: UpdateFinanceAssetData): Promise<{ success: boolean; message?: string }> {
    try {
      const { id, ...dataToUpdate } = updateData;
      
      const response = await makeAuthenticatedRequest(`/assets/${id}`, {
        method: 'PUT',
        body: JSON.stringify(dataToUpdate)
      });
      
      return response;
    } catch (error) {
      console.error('Error updating finance asset:', error);
      throw error;
    }
  }

  // Delete a finance asset
  async deleteFinanceAsset(assetId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/assets/${assetId}`, {
        method: 'DELETE'
      });
      
      return response;
    } catch (error) {
      console.error('Error deleting finance asset:', error);
      throw error;
    }
  }

  // Search finance assets
  async searchFinanceAssets(filters: FinanceAssetFilters): Promise<{ success: boolean; assets?: FinanceAsset[]; count?: number }> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      queryParams.append('user_id', filters.user_id);
      if (filters.category) queryParams.append('category', filters.category);
      if (filters.sub_category) queryParams.append('sub_category', filters.sub_category);
      if (filters.valuation_month) queryParams.append('valuation_month', filters.valuation_month);
      if (filters.search_term) queryParams.append('search_term', filters.search_term);
      if (filters.limit) queryParams.append('limit', filters.limit.toString());
      if (filters.offset) queryParams.append('offset', filters.offset.toString());
      
      const response = await makeAuthenticatedRequest(`/assets/search?${queryParams.toString()}`, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error searching finance assets:', error);
      throw error;
    }
  }

  // Get portfolio summary by category
  async getPortfolioSummary(userId: string, valuationMonth?: string): Promise<{ success: boolean; portfolio_summary?: PortfolioSummary[]; count?: number }> {
    try {
      const queryParams = new URLSearchParams();
      if (valuationMonth) queryParams.append('valuation_month', valuationMonth);
      
      const queryString = queryParams.toString();
      const url = `/assets/user/${userId}/portfolio-summary${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching portfolio summary:', error);
      throw error;
    }
  }

  // Get asset growth over time
  async getAssetGrowth(assetId: string, months?: number): Promise<{ success: boolean; asset_name?: string; growth_data?: AssetGrowthData[]; count?: number }> {
    try {
      const queryParams = new URLSearchParams();
      if (months) queryParams.append('months', months.toString());
      
      const queryString = queryParams.toString();
      const url = `/assets/${assetId}/growth${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching asset growth:', error);
      throw error;
    }
  }

  // Get portfolio value over time
  async getPortfolioValue(userId: string, months?: number): Promise<{ success: boolean; portfolio_data?: PortfolioValueData[]; count?: number }> {
    try {
      const queryParams = new URLSearchParams();
      if (months) queryParams.append('months', months.toString());
      
      const queryString = queryParams.toString();
      const url = `/assets/user/${userId}/portfolio-value${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching portfolio value:', error);
      throw error;
    }
  }

  // Get assets by category
  async getAssetsByCategory(userId: string, category: string, limit?: number): Promise<{ success: boolean; assets?: FinanceAsset[]; count?: number }> {
    try {
      const filters: FinanceAssetFilters = {
        user_id: userId,
        category: category as any,
        limit: limit || 50
      };
      
      return await this.getUserFinanceAssets(filters);
    } catch (error) {
      console.error('Error fetching assets by category:', error);
      throw error;
    }
  }

  // Get latest assets for a user (most recent valuation month)
  async getLatestAssets(userId: string, limit?: number): Promise<{ success: boolean; assets?: FinanceAsset[]; count?: number }> {
    try {
      const filters: FinanceAssetFilters = {
        user_id: userId,
        limit: limit || 50
      };
      
      return await this.getUserFinanceAssets(filters);
    } catch (error) {
      console.error('Error fetching latest assets:', error);
      throw error;
    }
  }

  // Get assets for a specific valuation month
  async getAssetsForMonth(userId: string, valuationMonth: string, limit?: number): Promise<{ success: boolean; assets?: FinanceAsset[]; count?: number }> {
    try {
      const filters: FinanceAssetFilters = {
        user_id: userId,
        valuation_month: valuationMonth,
        limit: limit || 50
      };
      
      return await this.getUserFinanceAssets(filters);
    } catch (error) {
      console.error('Error fetching assets for month:', error);
      throw error;
    }
  }

  // Update asset value (quick update for tracking)
  async updateAssetValue(assetId: string, newValue: number, valuationDate: string, valuationMonth: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/assets/${assetId}`, {
        method: 'PUT',
        body: JSON.stringify({
          value: newValue,
          valuation_date: valuationDate,
          valuation_month: valuationMonth
        })
      });
      
      return response;
    } catch (error) {
      console.error('Error updating asset value:', error);
      throw error;
    }
  }

  // Get asset change logs for a specific asset
  async getAssetLogs(assetId: string, limit?: number, offset?: number): Promise<{ 
    success: boolean; 
    logs?: AssetLog[]; 
    count?: number;
    asset_name?: string;
  }> {
    try {
      const queryParams = new URLSearchParams();
      if (limit) queryParams.append('limit', limit.toString());
      if (offset) queryParams.append('offset', offset.toString());
      
      const queryString = queryParams.toString();
      const url = `/assets/${assetId}/logs${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching asset logs:', error);
      throw error;
    }
  }

  // Get all asset change logs for a user
  async getUserAssetLogs(filters: AssetLogFilters): Promise<{ 
    success: boolean; 
    logs?: AssetLog[]; 
    count?: number;
  }> {
    try {
      const queryParams = new URLSearchParams();
      if (filters.asset_id) queryParams.append('asset_id', filters.asset_id);
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      if (filters.limit) queryParams.append('limit', filters.limit.toString());
      if (filters.offset) queryParams.append('offset', filters.offset.toString());
      
      const queryString = queryParams.toString();
      const url = `/assets/user/${filters.user_id}/logs${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching user asset logs:', error);
      throw error;
    }
  }

  // Get asset value history summary for a specific asset
  async getAssetValueHistory(assetId: string, months?: number): Promise<{ 
    success: boolean; 
    asset_name?: string;
    value_history?: AssetValueHistory[]; 
    count?: number;
  }> {
    try {
      const queryParams = new URLSearchParams();
      if (months) queryParams.append('months', months.toString());
      
      const queryString = queryParams.toString();
      const url = `/assets/${assetId}/value-history${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching asset value history:', error);
      throw error;
    }
  }

  // Get portfolio change summary for a user
  async getPortfolioChanges(userId: string, startDate?: string, endDate?: string, limit?: number): Promise<{ 
    success: boolean; 
    portfolio_changes?: PortfolioChange[]; 
    count?: number;
  }> {
    try {
      const queryParams = new URLSearchParams();
      if (startDate) queryParams.append('start_date', startDate);
      if (endDate) queryParams.append('end_date', endDate);
      if (limit) queryParams.append('limit', limit.toString());
      
      const queryString = queryParams.toString();
      const url = `/assets/user/${userId}/portfolio-changes${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      return response;
    } catch (error) {
      console.error('Error fetching portfolio changes:', error);
      throw error;
    }
  }

  // Convert API asset to our FinanceAsset interface
  convertApiAsset(apiAsset: any): FinanceAsset {
    // Safely parse the value to ensure it's a valid number
    let value = 0;
    if (apiAsset.value !== null && apiAsset.value !== undefined) {
      const parsedValue = typeof apiAsset.value === 'number' ? apiAsset.value : parseFloat(String(apiAsset.value));
      value = isNaN(parsedValue) || !isFinite(parsedValue) ? 0 : parsedValue;
    }

    return {
      id: apiAsset.id,
      user_id: apiAsset.user_id,
      name: apiAsset.name,
      notes: apiAsset.notes,
      category: apiAsset.category,
      sub_category: apiAsset.sub_category,
      value: value,
      valuation_date: apiAsset.valuation_date,
      valuation_month: apiAsset.valuation_month,
      created_at: apiAsset.created_at,
      updated_at: apiAsset.updated_at
    };
  }

  // Get recent asset changes for a user (last 30 days)
  async getRecentAssetChanges(userId: string, days: number = 30): Promise<{ 
    success: boolean; 
    logs?: AssetLog[]; 
    count?: number;
  }> {
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      
      const filters: AssetLogFilters = {
        user_id: userId,
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0],
        limit: 100
      };
      
      return await this.getUserAssetLogs(filters);
    } catch (error) {
      console.error('Error fetching recent asset changes:', error);
      throw error;
    }
  }

  // Get asset performance over time (simplified version of value history)
  async getAssetPerformance(assetId: string, months: number = 12): Promise<{
    success: boolean;
    asset_name?: string;
    performance?: {
      total_return: number;
      total_return_percentage: number;
      monthly_returns: Array<{
        month: string;
        value: number;
        change: number;
        change_percentage: number;
      }>;
      best_month: { month: string; change_percentage: number };
      worst_month: { month: string; change_percentage: number };
    };
  }> {
    try {
      const historyResponse = await this.getAssetValueHistory(assetId, months);
      
      if (!historyResponse.success || !historyResponse.value_history) {
        return { success: false };
      }

      const history = historyResponse.value_history;
      
      if (history.length < 2) {
        return { 
          success: true, 
          asset_name: historyResponse.asset_name,
          performance: {
            total_return: 0,
            total_return_percentage: 0,
            monthly_returns: [],
            best_month: { month: '', change_percentage: 0 },
            worst_month: { month: '', change_percentage: 0 }
          }
        };
      }

      // Calculate total return
      const firstValue = history[0].current_value;
      const lastValue = history[history.length - 1].current_value;
      const totalReturn = lastValue - firstValue;
      const totalReturnPercentage = firstValue > 0 ? (totalReturn / firstValue) * 100 : 0;

      // Build monthly returns
      const monthlyReturns = history.map(entry => ({
        month: entry.valuation_month,
        value: entry.current_value,
        change: entry.growth_amount || 0,
        change_percentage: entry.growth_percentage || 0
      }));

      // Find best and worst months
      const validReturns = monthlyReturns.filter(r => r.change_percentage !== 0);
      const bestMonth = validReturns.reduce((best, current) => 
        current.change_percentage > best.change_percentage ? current : best,
        { month: '', change_percentage: -Infinity }
      );
      const worstMonth = validReturns.reduce((worst, current) => 
        current.change_percentage < worst.change_percentage ? current : worst,
        { month: '', change_percentage: Infinity }
      );

      return {
        success: true,
        asset_name: historyResponse.asset_name,
        performance: {
          total_return: Math.round(totalReturn * 100) / 100,
          total_return_percentage: Math.round(totalReturnPercentage * 100) / 100,
          monthly_returns: monthlyReturns,
          best_month: bestMonth.change_percentage !== -Infinity ? bestMonth : { month: '', change_percentage: 0 },
          worst_month: worstMonth.change_percentage !== Infinity ? worstMonth : { month: '', change_percentage: 0 }
        }
      };
    } catch (error) {
      console.error('Error calculating asset performance:', error);
      throw error;
    }
  }

  // Get portfolio change summary for the last N days
  async getRecentPortfolioChanges(userId: string, days: number = 30): Promise<{ 
    success: boolean; 
    portfolio_changes?: PortfolioChange[]; 
    count?: number;
    summary?: {
      total_value_change: number;
      total_transactions: number;
      average_daily_change: number;
      positive_days: number;
      negative_days: number;
    };
  }> {
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      
      const response = await this.getPortfolioChanges(
        userId,
        startDate.toISOString().split('T')[0],
        endDate.toISOString().split('T')[0],
        days
      );

      if (!response.success || !response.portfolio_changes) {
        return response;
      }

      const changes = response.portfolio_changes;
      
      // Calculate summary statistics
      const totalValueChange = changes.reduce((sum, change) => sum + change.total_value_change, 0);
      const totalTransactions = changes.reduce((sum, change) => sum + change.total_changes, 0);
      const averageDailyChange = changes.length > 0 ? totalValueChange / changes.length : 0;
      const positiveDays = changes.filter(change => change.total_value_change > 0).length;
      const negativeDays = changes.filter(change => change.total_value_change < 0).length;

      return {
        ...response,
        summary: {
          total_value_change: Math.round(totalValueChange * 100) / 100,
          total_transactions: totalTransactions,
          average_daily_change: Math.round(averageDailyChange * 100) / 100,
          positive_days: positiveDays,
          negative_days: negativeDays
        }
      };
    } catch (error) {
      console.error('Error fetching recent portfolio changes:', error);
      throw error;
    }
  }

  // Helper function to format asset log entries for display
  formatAssetLog(log: AssetLog): {
    description: string;
    value_display: string;
    change_display: string;
    date_display: string;
    type: 'increase' | 'decrease' | 'initial' | 'deletion';
  } {
    let description = '';
    let type: 'increase' | 'decrease' | 'initial' | 'deletion' = 'initial';
    
    if (log.old_value === null) {
      description = `Initial value set for ${log.asset_name || 'asset'}`;
      type = 'initial';
    } else if (log.new_value === 0 && log.notes?.includes('deleted')) {
      description = `${log.asset_name || 'Asset'} was deleted`;
      type = 'deletion';
    } else if (log.value_change && log.value_change > 0) {
      description = `${log.asset_name || 'Asset'} value increased`;
      type = 'increase';
    } else if (log.value_change && log.value_change < 0) {
      description = `${log.asset_name || 'Asset'} value decreased`;
      type = 'decrease';
    } else {
      description = `${log.asset_name || 'Asset'} value updated`;
    }

    const valueDisplay = this.formatCurrency(log.new_value, 'VND');
    
    let changeDisplay = '';
    if (log.value_change !== null && log.value_change !== undefined) {
      const changeAmount = this.formatCurrency(Math.abs(log.value_change), 'VND');
      const changePercent = log.percentage_change ? `(${this.formatPercentage(log.percentage_change)})` : '';
      const changeSign = log.value_change >= 0 ? '+' : '-';
      changeDisplay = `${changeSign}${changeAmount} ${changePercent}`.trim();
    }

    const dateDisplay = new Date(log.created_at).toLocaleDateString();

    return {
      description,
      value_display: valueDisplay,
      change_display: changeDisplay,
      date_display: dateDisplay,
      type
    };
  }

  // Get asset statistics
  async getAssetStats(userId: string): Promise<{ 
    success: boolean; 
    stats?: {
      total_assets: number;
      total_value: number;
      categories: { [key: string]: number };
      latest_month: string;
      growth_this_month?: number;
      growth_percentage?: number;
    }
  }> {
    try {
      // Get all assets for the user
      const allAssetsResponse = await this.getUserFinanceAssets({ user_id: userId, limit: 1000 });
      
      if (!allAssetsResponse.success || !allAssetsResponse.assets) {
        return { success: false };
      }

      const assets = allAssetsResponse.assets;
      
      // Get latest month
      const latestMonth = assets.reduce((latest, asset) => {
        return asset.valuation_month > latest ? asset.valuation_month : latest;
      }, '');

      // Get assets for latest month
      const latestAssets = assets.filter(asset => asset.valuation_month === latestMonth);
      
      // Calculate category breakdown
      const categories: { [key: string]: number } = {};
      let totalValue = 0;
      
      latestAssets.forEach(asset => {
        categories[asset.category] = (categories[asset.category] || 0) + asset.value;
        totalValue += asset.value;
      });

      // Calculate growth (if we have previous month data)
      const previousMonth = new Date(latestMonth);
      previousMonth.setMonth(previousMonth.getMonth() - 1);
      const previousMonthStr = previousMonth.toISOString().split('T')[0].substring(0, 7) + '-01';
      
      const previousAssets = assets.filter(asset => asset.valuation_month === previousMonthStr);
      const previousTotalValue = previousAssets.reduce((sum, asset) => sum + asset.value, 0);
      
      let growth_this_month: number | undefined;
      let growth_percentage: number | undefined;
      
      if (previousTotalValue > 0) {
        growth_this_month = totalValue - previousTotalValue;
        growth_percentage = Math.round(((growth_this_month / previousTotalValue) * 100) * 100) / 100;
      }

      const stats = {
        total_assets: latestAssets.length,
        total_value: totalValue,
        categories,
        latest_month: latestMonth,
        growth_this_month,
        growth_percentage
      };

      return {
        success: true,
        stats
      };
    } catch (error) {
      console.error('Error fetching asset statistics:', error);
      throw error;
    }
  }

  // Helper function to format currency
  formatCurrency(amount: number, currency: string = 'USD'): string {
    // Handle NaN, null, undefined, and invalid numbers
    if (amount === null || amount === undefined || isNaN(amount) || !isFinite(amount)) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(0);
    }

    // Ensure amount is a valid number
    const validAmount = typeof amount === 'number' ? amount : parseFloat(String(amount));
    
    if (isNaN(validAmount) || !isFinite(validAmount)) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(0);
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(validAmount);
  }

  // Helper function to format percentage
  formatPercentage(value: number): string {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  }

  // Helper function to get current month string (YYYY-MM-01 format)
  getCurrentMonth(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}-01`;
  }

  // Helper function to get current date string (YYYY-MM-DD format)
  getCurrentDate(): string {
    return new Date().toISOString().split('T')[0];
  }

  // Validate asset data before submission
  validateAssetData(data: FinanceAssetFormData): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!data.name || data.name.trim().length === 0) {
      errors.push('Asset name is required');
    }

    if (data.value <= 0 || isNaN(data.value) || !isFinite(data.value)) {
      errors.push('Asset value must be a valid positive number');
    }

    if (!data.valuation_date) {
      errors.push('Valuation date is required');
    }

    if (!data.valuation_month) {
      errors.push('Valuation month is required');
    }

    // Validate date formats
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (data.valuation_date && !dateRegex.test(data.valuation_date)) {
      errors.push('Valuation date must be in YYYY-MM-DD format');
    }

    if (data.valuation_month && !dateRegex.test(data.valuation_month)) {
      errors.push('Valuation month must be in YYYY-MM-DD format');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Test function to verify NaN handling (for development)
  testNaNHandling(): void {
    if (process.env.NODE_ENV === 'development') {
      console.log('Testing NaN handling:');
      console.log('NaN:', this.formatCurrency(NaN, 'VND'));
      console.log('Undefined:', this.formatCurrency(undefined as any, 'VND'));
      console.log('Null:', this.formatCurrency(null as any, 'VND'));
      console.log('String "abc":', this.formatCurrency('abc' as any, 'VND'));
      console.log('Infinity:', this.formatCurrency(Infinity, 'VND'));
      console.log('Valid number 1000:', this.formatCurrency(1000, 'VND'));
    }
  }
}

export const financeAssetsService = new FinanceAssetsService();
